import { getViewAsUserId } from '../../../../scanline/utils/ajax';
import { hasAccessToken, getAccessToken } from '../../../../scanline/utils/token';
import { getCustomer, getUserName } from '../../../../util/user';

// purpose: standardize message formats sent over websocket to cloud services

/**
 * Available message types sent by client.
 */
const MESSAGE_TYPES = {
    CONNECT: 'CONNECT',
    REQUEST: 'REQUEST',
}

/**
 * Standardized "actions" that correspond to serverless.yml
 * websocket route actions.
 */
const STANDARD_ACTIONS = {
    CONNECT: 'CONNECT',
    UNKNOWN : 'UNKNOWN',
}

/**
 * Standardized subscription topics.
 */
const STANDARD_TOPICS = {
    ALL: 'ALL',
    UNKNOWN : 'UNKNOWN',
    NONE : 'NONE',
}

const BASE_IS_VALID_CLIENT_MESSAGE = `
Error: Invalid message payload for util.client.messages.createMessage()
    Schema: 
        - action (required) the action to map to serverless.yml's action.events.websocket.route
        - topic (required) the pubSubTopic which client PubSub will publish to
        - messageType (optional) a way to label and thus describe the unique schema of a message.  
        - body (optional) where any unique data (string or object) to pass values to the cloud service
        - requestToken (optional) a way to track individual requests, cloud service should return this if received.
    Errors:
`;

const UTIL_CLIENT_WS_MESSAGES = {
    MESSAGE_TYPES,
    STANDARD_ACTIONS,
    STANDARD_TOPICS,

    /**
     * Validates whether a client message payload conforms to the expected schema.
     *
     * @param {Object} payload - The client message payload
     * @param {string} payload.action - Required action string
     * @param {string} payload.topic  - Required topic string
     * @returns {{isValid: boolean, errorText?: string}}
     */
    isValidClientMessage: (payload = {}) => {
        const invalidActionValueMessage = payload.action ? undefined : `        - Missing prop "action"\n`;
        const invalidTopicValueMessage = payload.topic ? undefined : `        - Missing prop "topic"\n`;

        const errorItems = [invalidActionValueMessage, invalidTopicValueMessage].filter(v => !!v);

        let errorText = BASE_IS_VALID_CLIENT_MESSAGE;

        errorItems.forEach(item => {
            errorText += item;
        });

        if (errorItems.length) {
            return { isValid: false, errorText };
        }

        return { isValid: true }
    },

    /**
     * Throws an error if a client message is invalid.
     *
     * @param {Object} message - The client WebSocket message payload.
     * @param {Object} options - Options
     * @param {string} options.srcRef - Optional string to include in error for debugging
     * @throws {Error} If the message is invalid
     */
    errorOnInvalidMessage: (message = {}, { srcRef } ) => {
        const { isValid, errorText } = UTIL_CLIENT_WS_MESSAGES.isValidClientMessage(message);
        let _errorText = errorText;

        if (!isValid) {
            if (srcRef) {
                _errorText = `Reference: ${srcRef}\n\n${_errorText}`;
            }
            throw new Error(_errorText);
        }
    },

    /**
     * Creates a standardized client message object ready for WebSocket transmission.
     *
     * Adds authentication context automatically if available.
     *
     * @param {Object} payload
     * @param {string} payload.action - Required action
     * @param {string} payload.topic - Required topic
     * @param {string} [payload.messageType] - Defaults to MESSAGE_TYPES.REQUEST
     * @param {Object|string} [payload.body] - Optional custom payload body
     * @param {string} [payload.requestToken] - Optional request correlation token
     * @returns {Object} Standardized message object
     * @throws {Error} If schema validation fails
     */
    createMessage: (payload = {}) => {
        const { isValid, errorText } = UTIL_CLIENT_WS_MESSAGES.isValidClientMessage(payload);

        if (!isValid) {
            throw new Error(errorText);
        }

        return {
            ...payload,

            // required
            action: payload.action,
            topic: payload.topic,

            // optional
            messageType: payload?.messageType || MESSAGE_TYPES.REQUEST,
            body: payload?.body || undefined,
            requestToken: payload?.requestToken || undefined, // optional, allows client to identify duplicate requests and ignore inital requests in favor of most recent request.

            // auto generated
            authParams: {
                token: hasAccessToken() ? getAccessToken() : undefined,
                customer: getCustomer(),
                userId: getUserName(),
                viewAsUserId: getViewAsUserId() // should only apply to CorrLogix, should fail gracefully for other apps and simply be ignored.
            }
        }
    },

    /**
     * Convenience helper to create a standard "CONNECT" message.
     *
     * @returns {Object} message object with {action: CONNECT, topic: NONE, messageType: CONNECT}
     */
    createConnectMessage: () => {
        const { createMessage } = UTIL_CLIENT_WS_MESSAGES;
        return createMessage(
            { 
                messageType: MESSAGE_TYPES.CONNECT, 
                topic: STANDARD_TOPICS.NONE,
                // action: STANDARD_ACTIONS.CONNECT, // actions on connect will break
            }
        );
    }
}

export default UTIL_CLIENT_WS_MESSAGES;

